/* Mock Coupa/Ariba Origin Simulator.

   Route: /punchout/test-origin

   Lets you stand in the buyer's shoes pretending to be Coupa or Ariba:
     1. Click "Punch Out to UniBuy™" → calls our /origin/mock-posr
        endpoint which synthesises a PunchOutSetupRequest cXML.
     2. We display the cXML, the response cXML, and the start_page URL.
     3. Click the start page URL to land inside UniBuy™ with an active
        origin-PunchOut session attached (banner appears in the cart).
     4. After the buyer transfers the unified cart back, this page can
        display the captured PunchOutOrderMessage.

   This page is the entry point for demoing the FULL double-jump
   roundtrip to a procurement audience without needing a real Coupa.
*/
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Send, Loader2, FileCode, ExternalLink, Building2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ORIGINS = [
  { key: "COUPA",    name: "Coupa",       identity: "ACME-COUPA-001",       color: "#FE5000" },
  { key: "ARIBA",    name: "SAP Ariba",   identity: "JDECOFFEE-ARIBA-001",  color: "#0F6BFF" },
  { key: "AERCHAIN", name: "Aerchain",    identity: "JDECOFFEE-AERCHAIN-1", color: "#0EA5E9" },
];

export default function MockOriginPage() {
  const [params] = useSearchParams();
  const [from, setFrom] = useState(ORIGINS[0]);
  const [networkId, setNetworkId] = useState("JAMES.SMITH");
  const [companyName, setCompanyName] = useState("JDECOFFEE");
  const [countryCode, setCountryCode] = useState("NL");
  const [launching, setLaunching] = useState(false);
  const [posrResult, setPosrResult] = useState(null);
  const [returnedPoom, setReturnedPoom] = useState(null);

  // Pick up a returned cXML if UniBuy™ redirected the browser here.
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/punchout/session/${sid}`);
        if (data?.status === "transferred") {
          setReturnedPoom(data);
        }
      } catch (err) {
        // Session might be expired or not yet transferred — non-fatal,
        // page still renders with the launch form.
        console.debug("[MockOrigin] session lookup skipped:", err?.message);
      }
    })();
  }, [params]);

  const launchPosr = async () => {
    setLaunching(true);
    try {
      const { data } = await axios.post(`${API}/punchout/origin/mock-posr`, {
        from_domain: "DUNS",
        from_identity: from.identity,
        return_url: `/punchout/test-origin?session_id=__SID__`,
        network_id: networkId,
        company_name: companyName,
        country_code: countryCode,
      });
      // The mock endpoint stamps a fixed return URL with the placeholder; we
      // want the user's browser to actually come back here when UniBuy™
      // posts the PunchOutOrderMessage. Use the session id from the
      // response to build the local link.
      // Prefer relative path so the browser stays on the buyer's
      // current hostname (works seamlessly in preview / production).
      const sid = data?.session?.session_id;
      let startUrl = data.start_page_url || "";
      try {
        const u = new URL(startUrl);
        // If absolute and different origin, force relative
        if (u.origin !== window.location.origin) {
          startUrl = `${u.pathname}${u.search}`;
        }
      } catch {
        /* URL was already relative — no conversion needed */
      }
      setPosrResult({ ...data, computed_start_url: startUrl, return_anchor: `/punchout/test-origin?session_id=${sid}` });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "POSR launch failed");
    } finally {
      setLaunching(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 py-8">
      <div className="max-w-5xl mx-auto px-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-4"
        >
          <ArrowLeft size={14} /> Back to UniBuy™
        </Link>

        <div className="rounded-2xl border border-slate-700 bg-slate-800 p-6 shadow-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div
              className="h-10 w-10 rounded-md flex items-center justify-center font-display font-bold text-white shrink-0"
              style={{ background: from.color }}
            >
              {from.name.slice(0, 1)}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">
                eProcurement Origin Simulator
              </div>
              <h1 className="font-display text-2xl font-bold text-white">
                {from.name} → UniBuy™ double-jump test harness
              </h1>
            </div>
          </div>
          <p className="text-sm text-slate-300 leading-relaxed max-w-2xl">
            This page stands in for {from.name}. Pressing &ldquo;Punch Out to UniBuy™&rdquo;
            fires a real PunchOutSetupRequest cXML at UniBuy™. After your
            buyer assembles a unified cart inside UniBuy™ (including any
            sub-supplier items), the unified PunchOutOrderMessage will be
            posted back to <code className="text-xs bg-slate-900 px-1.5 py-0.5 rounded">/punchout/test-origin</code>.
          </p>

          {/* Origin picker */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="origin-picker">
            {ORIGINS.map((o) => (
              <button
                key={o.key}
                onClick={() => setFrom(o)}
                data-testid={`origin-option-${o.key}`}
                className={`rounded-lg border p-3 text-left transition-all ${
                  from.key === o.key
                    ? "border-white bg-slate-700"
                    : "border-slate-700 bg-slate-800 hover:border-slate-500"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="h-7 w-7 rounded-md flex items-center justify-center font-bold text-white"
                    style={{ background: o.color }}
                  >
                    {o.name.slice(0, 1)}
                  </div>
                  <div className="font-display font-semibold text-white text-sm">{o.name}</div>
                </div>
                <div className="text-[10px] font-mono text-slate-400 mt-1">{o.identity}</div>
              </button>
            ))}
          </div>

          {/* Buyer context */}
          <div className="mt-6 grid grid-cols-3 gap-3">
            <FieldSet label="Buyer Network ID">
              <Input
                value={networkId}
                onChange={(e) => setNetworkId(e.target.value)}
                data-testid="origin-network-id"
                className="bg-slate-900 border-slate-700 text-white"
              />
            </FieldSet>
            <FieldSet label="Company">
              <Input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                data-testid="origin-company"
                className="bg-slate-900 border-slate-700 text-white"
              />
            </FieldSet>
            <FieldSet label="Country (ISO-2)">
              <Input
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                data-testid="origin-country"
                className="bg-slate-900 border-slate-700 text-white"
              />
            </FieldSet>
          </div>

          <Button
            onClick={launchPosr}
            disabled={launching}
            data-testid="origin-launch-btn"
            className="mt-6 bg-white text-slate-900 hover:bg-slate-200 font-semibold rounded-md px-5 py-5"
          >
            {launching ? (
              <><Loader2 size={14} className="animate-spin mr-2" /> Posting cXML…</>
            ) : (
              <><Send size={14} className="mr-2" /> Punch Out to UniBuy™ <ArrowRight size={14} className="ml-1" /></>
            )}
          </Button>
        </div>

        {/* POSR result */}
        {posrResult && (
          <div className="mt-6 rounded-2xl border border-emerald-700 bg-emerald-950/40 p-6" data-testid="origin-posr-result">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-300">
                PunchOutSetupResponse received
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <CxmlBlock title="Outbound PunchOutSetupRequest" body={posrResult.posr_cxml} />
              </div>
              <div>
                <CxmlBlock title="Inbound PunchOutSetupResponse" body={posrResult.response_cxml} />
              </div>
            </div>
            <a
              href={posrResult.computed_start_url}
              data-testid="origin-start-link"
              className="mt-4 inline-flex items-center gap-1.5 bg-emerald-400 hover:bg-emerald-300 text-emerald-950 font-bold rounded-md px-4 py-2.5 text-sm"
            >
              <ExternalLink size={14} /> Open UniBuy™ in this PunchOut session →
            </a>
            <div className="mt-2 text-[11px] text-slate-400 font-mono break-all">
              Browser will return to: {posrResult.return_anchor}
            </div>
          </div>
        )}

        {/* Returned POOM after transfer */}
        {returnedPoom && (
          <div className="mt-6 rounded-2xl border border-amber-700 bg-amber-950/30 p-6" data-testid="origin-poom-result">
            <div className="flex items-center gap-2 mb-3">
              <Building2 size={14} className="text-amber-300" />
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-300">
                PunchOutOrderMessage received from UniBuy™
              </div>
            </div>
            <div className="rounded-md bg-slate-900 border border-slate-700 p-3 mb-4">
              <div className="grid grid-cols-3 gap-3 text-xs">
                <Stat label="Items" value={returnedPoom.cart_snapshot?.length ?? 0} />
                <Stat label="Currency" value={returnedPoom.currency || "—"} />
                <Stat
                  label="Total"
                  value={
                    (returnedPoom.cart_snapshot || []).reduce(
                      (s, i) => s + (i.unit_price || 0) * (i.quantity || 0),
                      0
                    ).toFixed(2)
                  }
                />
              </div>
            </div>
            <div className="rounded-md bg-slate-900 border border-slate-700 p-3 max-h-72 overflow-y-auto text-[11px] text-slate-300">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">
                Cart lines (UniBuy™ PNs land in your ERP — sub-supplier IDs are private to UniBuy™)
              </div>
              <table className="w-full">
                <thead>
                  <tr className="text-slate-500">
                    <th className="text-left font-medium pb-1">UniBuy™ PN</th>
                    <th className="text-left font-medium pb-1">MFG PN</th>
                    <th className="text-left font-medium pb-1">Description</th>
                    <th className="text-right font-medium pb-1">Qty</th>
                    <th className="text-right font-medium pb-1">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {(returnedPoom.cart_snapshot || []).map((l, i) => (
                    <tr key={i} className="border-t border-slate-800">
                      <td className="py-1 font-mono">{l.infoshop_pn}</td>
                      <td className="py-1 font-mono">{l.mfg_pn || "—"}</td>
                      <td className="py-1">{l.description}</td>
                      <td className="py-1 text-right">{l.quantity}</td>
                      <td className="py-1 text-right">{l.unit_price?.toFixed?.(2) ?? l.unit_price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FieldSet({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{label}</div>
      {children}
    </label>
  );
}

function CxmlBlock({ title, body }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1.5 inline-flex items-center gap-1">
        <FileCode size={11} /> {title}
      </div>
      <pre className="rounded-md bg-slate-900 border border-slate-700 p-3 text-[10px] font-mono text-slate-300 max-h-72 overflow-auto whitespace-pre-wrap break-all">
        {body}
      </pre>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{label}</div>
      <div className="font-display text-lg font-bold text-white">{value}</div>
    </div>
  );
}
